import { useEffect, useState, useCallback } from "react"
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import axios from "axios"
import moment from "moment"
import ActionBar from "@/components/core/ActionBar"
import Pagination from "@/components/core/Pagination"
import { APIService } from "@/common/API"
import Settings from "@/common/Settings"
import { parsedUser } from "@/common/GetCurrentUser"
import { Routes } from "@/routes"
import IncidentStatusBadge from "@/components/Incidents/IncidentStatusBadge"
import IncidentKPICards from "@/components/Incidents/IncidentKPICards"
import IncidentCharts from "@/components/Incidents/IncidentCharts"

interface Filters {
  dateFrom: string
  dateTo: string
  status: string
  department: string
  incidentType: string
  agentId: string
  search: string
}

const EMPTY_FILTERS: Filters = {
  dateFrom: "", dateTo: "", status: "", department: "",
  incidentType: "", agentId: "", search: "",
}

const INCIDENT_TYPES = [
  "Entrega tardía", "Entrega incompleta", "Entrega errada", "Mercancía averiada",
  "No entrega", "Servicio chofer", "Servicio almacén", "Servicio tienda",
  "Entrega a persona incorrecta", "Producto usado", "Documentación incorrecta",
]

const DEPARTMENTS = [
  "Ventas Externas", "Tienda Prol. 27 Feb", "Almacén Prol. 27 Feb", "Showroom", "E-Commerce",
]

const PAGE_SIZE = 40

const IncidentsPage = () => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const user: any = parsedUser()

  const [records, setRecords] = useState<any[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS)
  const [agents, setAgents] = useState<any[]>([])
  const [dashboard, setDashboard] = useState<any>(null)
  const [dashLoading, setDashLoading] = useState(false)

  const buildQs = (f: Filters, pg: number) => {
    const p: string[] = [`OrganizationId=${user.OrganizationId}`, `Page=${pg}`, `Quantity=${PAGE_SIZE}`]
    if (f.dateFrom) p.push(`DateFrom=${f.dateFrom}`)
    if (f.dateTo) p.push(`DateTo=${f.dateTo}`)
    if (f.status) p.push(`Status=${encodeURIComponent(f.status)}`)
    if (f.department) p.push(`Department=${encodeURIComponent(f.department)}`)
    if (f.incidentType) p.push(`IncidentType=${encodeURIComponent(f.incidentType)}`)
    if (f.agentId) p.push(`AgentId=${f.agentId}`)
    if (f.search) p.push(`Search=${encodeURIComponent(f.search)}`)
    return p.join("&")
  }

  const loadRecords = useCallback(async (f: Filters, pg: number) => {
    setIsLoading(true)
    try {
      const res = await APIService.getAction("Incident/List", buildQs(f, pg))
      if (res.data.status === "ok") {
        setRecords(res.data.response)
        setTotalPages(res.data.quantityOfPages ?? 1)
        setTotalRecords(res.data.quantityOfRecords ?? 0)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loadDashboard = useCallback(async (f: Filters) => {
    setDashLoading(true)
    try {
      const p = [`OrganizationId=${user.OrganizationId}`]
      if (f.dateFrom) p.push(`DateFrom=${f.dateFrom}`)
      if (f.dateTo) p.push(`DateTo=${f.dateTo}`)
      const res = await APIService.getAction("Incident/Dashboard", p.join("&"))
      if (res.data.status === "ok") setDashboard(res.data.response)
    } finally {
      setDashLoading(false)
    }
  }, [])

  const loadAgents = useCallback(async () => {
    const res = await APIService.getAction("users/list", `organizationId=${user.OrganizationId}&Quantity=200`)
    if (res.data.status === "ok") setAgents(res.data.response ?? [])
  }, [])

  useEffect(() => { loadAgents() }, [])
  useEffect(() => { loadRecords(applied, page) }, [applied, page])
  useEffect(() => { loadDashboard(applied) }, [applied])

  const handleApply = () => { setApplied({ ...filters }); setPage(0) }
  const handleClear = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(0) }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const p: string[] = [`OrganizationId=${user.OrganizationId}`]
      if (applied.dateFrom) p.push(`DateFrom=${applied.dateFrom}`)
      if (applied.dateTo) p.push(`DateTo=${applied.dateTo}`)
      if (applied.status) p.push(`Status=${encodeURIComponent(applied.status)}`)
      if (applied.department) p.push(`Department=${encodeURIComponent(applied.department)}`)
      if (applied.incidentType) p.push(`IncidentType=${encodeURIComponent(applied.incidentType)}`)
      if (applied.agentId) p.push(`AgentId=${applied.agentId}`)
      const token = localStorage.getItem("LogedUser")
      const url = `${Settings.BasePathForAPI}Incident/Export?${p.join("&")}`
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      const href = URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = href
      link.download = `incidents_${moment().format("YYYYMMDD_HHmm")}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } finally {
      setIsExporting(false)
    }
  }

  const setFilter = (key: keyof Filters, value: string) =>
    setFilters(prev => ({ ...prev, [key]: value }))

  return (
    <Container fluid className="px-4 py-3">
      <ActionBar
        title={t("Incidencias")}
        icon="fas fa-exclamation-circle text-danger"
        actions={
          <Button variant="outline-secondary" size="sm" onClick={handleExport} disabled={isExporting}>
            {isExporting ? <Spinner animation="border" size="sm" /> : <><i className="fas fa-download me-1" />{t("Exportar CSV")}</>}
          </Button>
        }
      />

      <IncidentKPICards data={dashboard} isLoading={dashLoading} />
      {dashboard && (
        <IncidentCharts byType={dashboard.byType ?? []} trend={dashboard.trend ?? []} />
      )}

      <Card className="mb-3 shadow-sm border-0">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="small">{t("From")}</Form.Label>
              <Form.Control type="date" size="sm" value={filters.dateFrom}
                onChange={e => setFilter("dateFrom", e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t("To")}</Form.Label>
              <Form.Control type="date" size="sm" value={filters.dateTo}
                onChange={e => setFilter("dateTo", e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t("Status")}</Form.Label>
              <Form.Select size="sm" value={filters.status} onChange={e => setFilter("status", e.target.value)}>
                <option value="">{t("All")}</option>
                <option value="New">{t("Nuevo")}</option>
                <option value="Under Review">{t("En revisión")}</option>
                <option value="Resolved">{t("Resuelto")}</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t("Dependencia")}</Form.Label>
              <Form.Select size="sm" value={filters.department} onChange={e => setFilter("department", e.target.value)}>
                <option value="">{t("All")}</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t("Tipo de incidencia")}</Form.Label>
              <Form.Select size="sm" value={filters.incidentType} onChange={e => setFilter("incidentType", e.target.value)}>
                <option value="">{t("All")}</option>
                {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t("Agents")}</Form.Label>
              <Form.Select size="sm" value={filters.agentId} onChange={e => setFilter("agentId", e.target.value)}>
                <option value="">{t("All")}</option>
                {agents.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.firstName} {a.lastName}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small">{t("Search")}</Form.Label>
              <Form.Control type="text" size="sm" value={filters.search}
                placeholder={t("Search") + "..."}
                onChange={e => setFilter("search", e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleApply()}
              />
            </Col>
            <Col md="auto" className="d-flex gap-2">
              <Button size="sm" onClick={handleApply}>{t("Filter")}</Button>
              <Button size="sm" variant="outline-secondary" onClick={handleClear}>{t("Clear")}</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>{t("Fecha")}</th>
                  <th>{t("Nombre")}</th>
                  <th>{t("Dependencia")}</th>
                  <th>{t("Número de pedido")}</th>
                  <th>{t("Tipo de incidencia")}</th>
                  <th>{t("Status")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-muted py-4">{t("Sin incidencias")}</td></tr>
                ) : records.map((r: any) => (
                  <tr key={r.id} style={{ cursor: "pointer" }}
                    onClick={() => history.push(`${Routes.IncidentDetails.clean}${r.id}`)}>
                    <td className="text-nowrap">{moment(r.reportDate).format("DD/MM/YY")}</td>
                    <td>{r.agentName}</td>
                    <td>{r.department}</td>
                    <td>{r.orderNumber}</td>
                    <td>
                      {r.incidentType?.split(",").slice(0, 2).map((t: string) => (
                        <Badge key={t} bg="light" text="dark" className="me-1 border">{t.trim()}</Badge>
                      ))}
                      {r.incidentType?.split(",").length > 2 && (
                        <Badge bg="secondary">+{r.incidentType.split(",").length - 2}</Badge>
                      )}
                    </td>
                    <td><IncidentStatusBadge status={r.status} /></td>
                    <td>
                      <Button size="sm" variant="outline-primary"
                        onClick={e => { e.stopPropagation(); history.push(`${Routes.IncidentDetails.clean}${r.id}`) }}>
                        <i className="fas fa-eye" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="mt-3">
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            totalRecords={totalRecords}
            onPageChanged={(p: number) => setPage(p)}
          />
        </div>
      )}
    </Container>
  )
}

export default IncidentsPage
